import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { aggregate, defaultWeightsFrom, scoreColor } from './macroScore';
import * as presetStore from './macroScorePresetStore';
import MacroScoreMethodologyModal from './MacroScoreMethodologyModal';
import ScorePredictiveModal from './ScorePredictiveModal';

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

// Semicircle gauge (−90°→90°, clockwise over the top).
function polar(cx, cy, r, angleDeg) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}
function arcPath(cx, cy, r, startAngle, endAngle) {
  const start = polar(cx, cy, r, endAngle);
  const end = polar(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

function Gauge({ value }) {
  const cx = 90;
  const cy = 84;
  const r = 72;
  const f = value == null ? 0 : Math.max(0, Math.min(100, value)) / 100;
  const color = scoreColor(value);
  return (
    <svg width="180" height="100" viewBox="0 0 180 100" role="img" aria-label="score gauge">
      <path d={arcPath(cx, cy, r, -90, 90)} stroke="#374151" strokeWidth="12" fill="none" strokeLinecap="round" />
      {f > 0 && (
        <path
          d={arcPath(cx, cy, r, -90, -90 + 180 * f)}
          stroke={color}
          strokeWidth="12"
          fill="none"
          strokeLinecap="round"
        />
      )}
      <text x={cx} y={cy - 8} textAnchor="middle" fontSize="32" fontWeight="700" fill={color}>
        {value == null ? '—' : Math.round(value)}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" fontSize="10" fill="#9ca3af">
        out of 100
      </text>
    </svg>
  );
}

function CategoryBreakdown({ result }) {
  if (!result?.categories?.length) return null;
  const signalsByKey = Object.fromEntries((result.signals || []).map((s) => [s.key, s]));
  return (
    <div className="mt-3 space-y-2">
      {result.categories.map((cat) => (
        <div key={cat.name} className="border border-gray-800 rounded-md p-2">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs font-semibold text-gray-300">{cat.name}</span>
            <span className="text-xs font-bold" style={{ color: scoreColor(cat.subscore) }}>
              {cat.subscore == null ? '—' : Math.round(cat.subscore)}
            </span>
          </div>
          <div className="space-y-0.5">
            {cat.signals.map((key) => {
              const s = signalsByKey[key];
              if (!s) return null;
              return (
                <div key={key} className="flex justify-between text-[11px] text-gray-400">
                  <span className={s.subscore == null ? 'italic text-gray-600' : ''}>{s.label}</span>
                  <span>
                    {s.subscore == null ? (
                      <span className="text-gray-600">no data</span>
                    ) : (
                      <span style={{ color: scoreColor(s.subscore) }}>{Math.round(s.subscore)}</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ScoreCard({ title, subtitle, result, expanded, onToggle }) {
  const color = scoreColor(result?.value);
  const pct = result?.coverage != null ? Math.round(result.coverage * 100) : 100;
  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 flex flex-col">
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-200">{title}</h3>
          <p className="text-[11px] text-gray-500">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-4 mt-2">
        <Gauge value={result?.value} />
        <div>
          <div className="text-lg font-bold uppercase tracking-wide" style={{ color }}>
            {result?.label || '—'}
          </div>
          {result?.degraded && result?.value != null && (
            <div className="text-[11px] text-yellow-500/80 mt-1">
              based on {pct}% of intended signals
            </div>
          )}
          {result?.value == null && (
            <div className="text-[11px] text-gray-500 mt-1">no data available</div>
          )}
        </div>
      </div>
      <button
        onClick={onToggle}
        className="mt-2 text-xs text-blue-400 hover:text-blue-300 self-start"
      >
        {expanded ? 'Hide breakdown ▲' : 'Show breakdown ▼'}
      </button>
      {expanded && <CategoryBreakdown result={result} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Score-history chart (default-weighted, ~10yr monthly from the backend)
// ---------------------------------------------------------------------------

function ScoreHistoryChart({ history, onOpen }) {
  const data = useMemo(
    () =>
      (history || []).map((p) => ({
        date: p.date,
        year: (p.date || '').slice(0, 4),
        economic: p.economic_health,
        sentiment: p.market_sentiment,
      })),
    [history],
  );
  if (data.length < 2) return null;

  // Sparse year ticks (first month of each distinct year).
  const yearTicks = [];
  const seen = new Set();
  for (const d of data) {
    if (!seen.has(d.year)) {
      seen.add(d.year);
      yearTicks.push(d.date);
    }
  }

  return (
    <div
      className="mt-4 border-t border-gray-800 pt-3 cursor-pointer group rounded-md transition-colors hover:bg-gray-900/40"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen?.(); }
      }}
      title="Compare these scores against the S&P 500"
    >
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-sm font-semibold text-gray-200">Score history</h3>
        <span className="text-[11px] text-blue-400 group-hover:text-blue-300">
          Compare to S&amp;P 500 →
        </span>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={data} margin={{ top: 5, right: 12, bottom: 0, left: -16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis
            dataKey="date"
            ticks={yearTicks}
            tickFormatter={(d) => (d || '').slice(0, 4)}
            tick={{ fill: '#9ca3af', fontSize: 11 }}
            stroke="#374151"
          />
          <YAxis domain={[0, 100]} tick={{ fill: '#9ca3af', fontSize: 11 }} stroke="#374151" />
          <Tooltip
            contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 12 }}
            labelStyle={{ color: '#9ca3af' }}
            formatter={(v, name) => [v == null ? '—' : Math.round(v), name]}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line
            type="monotone"
            dataKey="economic"
            name="Economic Health"
            stroke="#34d399"
            dot={false}
            strokeWidth={2}
            connectNulls={false}
          />
          <Line
            type="monotone"
            dataKey="sentiment"
            name="Market Sentiment"
            stroke="#60a5fa"
            dot={false}
            strokeWidth={2}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
      <p className="text-[10px] text-gray-500 mt-1">
        Reconstructed with no look-ahead. Market Sentiment history is built from the deep-history
        signals (VIX + index momentum); Fear &amp; Greed, Bitcoin, and Fed odds apply to the live
        score only. Reflects default weights, not your slider tuning.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weight sliders
// ---------------------------------------------------------------------------

function WeightSliders({ title, signals, weights, onChange }) {
  // Group signals by category, preserving first-seen order.
  const groups = useMemo(() => {
    const m = new Map();
    for (const s of signals || []) {
      if (!m.has(s.category)) m.set(s.category, []);
      m.get(s.category).push(s);
    }
    return Array.from(m.entries());
  }, [signals]);

  const total = Object.values(weights || {}).reduce((a, b) => a + (Number(b) || 0), 0) || 1;

  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-300 mb-2">{title}</h4>
      <div className="space-y-3">
        {groups.map(([cat, sigs]) => (
          <div key={cat}>
            <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{cat}</div>
            {sigs.map((s) => {
              const w = Number(weights?.[s.key]) || 0;
              return (
                <div key={s.key} className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] text-gray-400 w-40 truncate" title={s.label}>
                    {s.label}
                  </span>
                  <input
                    type="range"
                    min="0"
                    max="0.4"
                    step="0.005"
                    value={w}
                    onChange={(e) => onChange(s.key, parseFloat(e.target.value))}
                    className="flex-1 accent-blue-500"
                  />
                  <span className="text-[11px] text-gray-300 w-10 text-right">
                    {Math.round((w / total) * 100)}%
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

function MacroScorePanel({ data, spyHistory }) {
  const defaults = useMemo(() => defaultWeightsFrom(data), [data]);
  const [weights, setWeights] = useState(defaults);
  const [expanded, setExpanded] = useState({ eco: false, mkt: false });
  const [showWeights, setShowWeights] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);
  const [showPredictive, setShowPredictive] = useState(false);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [newName, setNewName] = useState('');
  const [status, setStatus] = useState(null);

  // Seed working weights from the server defaults, but RE-seed only when the
  // default weights actually change in content — not on every dashboard
  // refetch (which produces a new `data`/`defaults` object reference). Keying
  // the effect on the stringified defaults preserves the user's slider tuning
  // and loaded preset across a refresh.
  const defaultsKey = JSON.stringify(defaults);
  useEffect(() => {
    setWeights(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultsKey]);

  const loadPresets = useCallback(async () => {
    try {
      const list = await presetStore.listPresets();
      setPresets(list);
    } catch (e) {
      // Presets are optional — a failure here shouldn't break the panel.
      setPresets([]);
    }
  }, []);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  const ecoSignals = data?.economic_health?.signals || [];
  const mktSignals = data?.market_sentiment?.signals || [];

  // Live re-aggregation — pure, no network.
  const ecoResult = useMemo(
    () => aggregate(ecoSignals, weights.economic_health || {}, 'economic'),
    [ecoSignals, weights.economic_health],
  );
  const mktResult = useMemo(
    () => aggregate(mktSignals, weights.market_sentiment || {}, 'sentiment'),
    [mktSignals, weights.market_sentiment],
  );

  const setWeight = (family, key, value) => {
    setWeights((prev) => ({
      ...prev,
      [family]: { ...prev[family], [key]: value },
    }));
  };

  const handleReset = () => {
    setWeights(defaults);
    setSelectedPreset('');
    setStatus('Reset to default weights');
  };

  const handleLoad = async (id) => {
    setSelectedPreset(id);
    if (!id) {
      setWeights(defaults);
      return;
    }
    try {
      const preset = await presetStore.fetchPreset(id);
      setWeights({
        economic_health: { ...defaults.economic_health, ...(preset.weights?.economic_health || {}) },
        market_sentiment: { ...defaults.market_sentiment, ...(preset.weights?.market_sentiment || {}) },
      });
      setStatus(`Loaded “${preset.name}”`);
    } catch (e) {
      setStatus('Could not load preset');
    }
  };

  const handleSave = async () => {
    const name = newName.trim();
    if (!name) {
      setStatus('Enter a name first');
      return;
    }
    try {
      await presetStore.createPreset({ name, weights });
      setNewName('');
      setStatus(`Saved “${name}”`);
      await loadPresets();
    } catch (e) {
      const msg = e?.response?.data?.name?.[0] || e?.response?.data?.weights?.[0] || 'Save failed';
      setStatus(msg);
    }
  };

  if (!data) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 text-sm text-gray-500">
        Composite score unavailable.
      </div>
    );
  }

  return (
    <div className="bg-gray-950/60 border border-gray-800 rounded-lg p-4">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h2 className="text-base font-semibold text-white">Composite Scores</h2>
          <p className="text-xs text-gray-500">
            Two 0–100 reads: economic fundamentals vs. market mood. Tune the weights to your view.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowMethodology(true)}
            className="px-3 py-1.5 text-xs rounded-md border border-blue-500/60 text-blue-300 hover:bg-blue-600/20"
          >
            ⓘ How is this calculated?
          </button>
          <button
            onClick={() => setShowWeights((v) => !v)}
            className="px-3 py-1.5 text-xs rounded-md border border-gray-600 text-gray-300 hover:bg-gray-700/40"
          >
            {showWeights ? 'Hide weights' : 'Adjust weights'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ScoreCard
          title="Economic Health"
          subtitle="FRED hard data · inflation, labor, growth, housing…"
          result={ecoResult}
          expanded={expanded.eco}
          onToggle={() => setExpanded((e) => ({ ...e, eco: !e.eco }))}
        />
        <ScoreCard
          title="Market Sentiment"
          subtitle="Fear & Greed · index momentum · VIX · BTC · Fed"
          result={mktResult}
          expanded={expanded.mkt}
          onToggle={() => setExpanded((e) => ({ ...e, mkt: !e.mkt }))}
        />
      </div>

      <ScoreHistoryChart history={data.history} onOpen={() => setShowPredictive(true)} />

      {showWeights && (
        <div className="mt-4 border-t border-gray-800 pt-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <select
              value={selectedPreset}
              onChange={(e) => handleLoad(e.target.value)}
              className="bg-gray-800 border border-gray-600 rounded-md text-xs text-gray-200 px-2 py-1"
            >
              <option value="">Default weights</option>
              {presets.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New preset name"
              className="bg-gray-800 border border-gray-600 rounded-md text-xs text-gray-200 px-2 py-1"
            />
            <button
              onClick={handleSave}
              className="px-3 py-1 text-xs rounded-md border border-green-600/60 text-green-300 hover:bg-green-600/20"
            >
              Save
            </button>
            <button
              onClick={handleReset}
              className="px-3 py-1 text-xs rounded-md border border-gray-600 text-gray-300 hover:bg-gray-700/40"
            >
              Reset
            </button>
            {status && <span className="text-xs text-gray-400">{status}</span>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <WeightSliders
              title="Economic Health weights"
              signals={ecoSignals}
              weights={weights.economic_health}
              onChange={(key, value) => setWeight('economic_health', key, value)}
            />
            <WeightSliders
              title="Market Sentiment weights"
              signals={mktSignals}
              weights={weights.market_sentiment}
              onChange={(key, value) => setWeight('market_sentiment', key, value)}
            />
          </div>
        </div>
      )}

      <MacroScoreMethodologyModal
        isOpen={showMethodology}
        onClose={() => setShowMethodology(false)}
        eco={ecoResult}
        mkt={mktResult}
        methodology={data.methodology}
      />

      <ScorePredictiveModal
        isOpen={showPredictive}
        onClose={() => setShowPredictive(false)}
        scoreHistory={data.history || []}
        spyHistory={spyHistory}
      />
    </div>
  );
}

export default MacroScorePanel;
